import { useMemo } from 'react';

import { mapArtifactToSessionReflectionRow } from './sessionReflectionHomeUtils';
import { useMemberReflectionHighlightCounts } from './useMemberReflectionHighlightCounts';

import {
  isMemberReflectionProfile,
  MEMBER_REFLECTION_PROFILE_SLUG,
  MEMBER_REFLECTION_PROFILE_SLUGS,
} from '@/components/Sessions/memberReflectionProfiles';
import { useGetMomentsMemberArtifactsQuery } from '@/store/api/momentsApi';

export { MEMBER_REFLECTION_PROFILE_SLUG };
export const HOME_REFLECTION_LIMIT = 3;

export type SessionReflectionsHomeStatus = 'error' | 'loading' | 'ready';

export const useMemberSessionReflectionsHome = () => {
  const artifactsArg = {
    includeContent: true,
    limit: HOME_REFLECTION_LIMIT,
    profileSlug: MEMBER_REFLECTION_PROFILE_SLUGS,
  };
  const { data, isError, isLoading } =
    useGetMomentsMemberArtifactsQuery(artifactsArg);

  const artifacts = useMemo(
    () =>
      (data?.items ?? []).filter((artifact) =>
        isMemberReflectionProfile(artifact.profileSlug),
      ),
    [data?.items],
  );
  const artifactIds = useMemo(
    () => artifacts.map((artifact) => artifact.artifactId),
    [artifacts],
  );
  const highlightCounts = useMemberReflectionHighlightCounts(artifactIds);

  const rows = useMemo(
    () =>
      artifacts.map((artifact) =>
        // Explicit arrow: passing the mapper straight to .map would feed it the
        // array index as the highlight count.
        mapArtifactToSessionReflectionRow(
          artifact,
          highlightCounts[artifact.artifactId] ?? 0,
        ),
      ),
    [artifacts, highlightCounts],
  );

  let status: SessionReflectionsHomeStatus = 'ready';
  if (isLoading) {
    status = 'loading';
  } else if (isError && rows.length === 0) {
    status = 'error';
  }

  return {
    isLoading: status === 'loading',
    rows,
    status,
  };
};
